import { useModal } from "@/app/shared/modal-views/use-modal";
import { Checkbox, Text, Button, Badge, cn, Tooltip } from "rizzui";
import { VscVerifiedFilled } from "react-icons/vsc";
import {
  IEnquiryEntry,
  EActionTrackingType,
  EContactSaleSubmissionTag,
  EOTPStatus,
} from "@/utilities/types/EnquiryEntry";
import formatPhone from "@/utilities/functions/format-phone";
import PhoneIcon from "@/components/icons/phone.tsx";
import { Notes } from "@/app/app-shared/enquiry-entrties/notes";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback } from "react";
import { RiWhatsappLine } from "react-icons/ri";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";
import DeletePopover from "@/app/shared/delete-popover";
import { getLeadSourceDisplay } from "@/utilities/functions/getLeadSourceDisplay";
import LeadDetailsPanel from "../desktop/LeadDetailsPanel";
import useProfile from "@/hooks/use-profile";
import { IoMdClose } from "react-icons/io";
import { getLeadTag } from "@/utilities/functions/getLeadTag";
import { Roles } from "@/utilities/types/Users";
import { capitalizeWords } from "@/utilities/functions/capitalizeWords";

dayjs.extend(relativeTime);
const GREY = "bg-gray-100 text-gray-600";
const RED = "bg-red-100 text-red-600";
const GREEN = "bg-[#e4ede1] text-[#0d920a]";
const ORANGE = "bg-[#FFD9BB] text-[#F66F05]";
const WARM = "bg-[#FFF3C1] text-[#F78602]";

interface EnquiryEntryCardProps {
  lead: IEnquiryEntry;
  isSelected?: boolean;
  onSelect?: () => void;
  onDelete: (id: string) => void;
  isAdmin?: boolean;
  onRefresh?: () => void;
  showCheckbox?: boolean;
  isViewInDetail?: boolean;
  onAgentClick?: (agentId: string) => void;
}

export default function EnquiryEntryCard({
  lead,
  isSelected,
  onSelect,
  onDelete,
  isAdmin,
  onRefresh,
  showCheckbox = true,
  isViewInDetail = false,
  onAgentClick,
}: EnquiryEntryCardProps) {
  const { openModal, closeModal } = useModal();
  const { profile } = useProfile();

  const handleCallClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      window.location.href = `tel:${lead.phone}`;
    },
    [lead.phone]
  );

  const handleWhatsAppClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(`https://wa.me/${lead.phone}`, "_blank");
    },
    [lead.phone]
  );

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openModal({
      view: (
        <Notes
          data={lead}
          isModalView
          onRefresh={onRefresh}
          isAdmin={isAdmin}
        />
      ),
      customSize: "1000px",
      size: "xl",
    });
  };

  const handleDetailsClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await EnquiryEntriesRepository.createLogAction({
      submissionId: lead.id,
      trackingType: EActionTrackingType.VIEW_DETAIL,
    });

    openModal({
      view: (
        <div className="w-full">
          <LeadDetailsPanel
            selectedLead={lead}
            userRole={profile?.role}
            onRefresh={onRefresh}
            renderCloseButton={() => (
              <Button
                variant="text"
                onClick={closeModal}
                className="hover:text-white !p-0"
              >
                <IoMdClose size={30} />
              </Button>
            )}
          />
        </div>
      ),
      customSize: "100%",
      size: "full",
    });
  };

  const handleAgentClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (lead.user?.id && onAgentClick) {
        onAgentClick(lead.user.id);
      }
    },
    [lead.user?.id, onAgentClick]
  );

  const renderAgent = useCallback(() => {
    if (!lead.user?.firstName || !lead.user?.lastName || !isAdmin) return null;

    const name = `${lead.user.firstName} ${lead.user.lastName}`.trim();

    if (!name) return null;

    return (
      <div className="w-full flex flex-row gap-1 mb-2">
        <Text className="text-gray-900 font-semibold">Agent</Text>
        <span>•</span>
        <Text
          className="text-gray-900 font-semibold max-w-fit text-ellipsis overflow-hidden text-nowrap hover:underline cursor-pointer transition-colors duration-200"
          onClick={handleAgentClick}
        >
          {name}
        </Text>
      </div>
    );
  }, [lead.user, isAdmin, handleAgentClick]);

  const renderBadge = useCallback(() => {
    if (lead.contactSaleSubmissionReport)
      return (
        <Badge
          className={cn("font-semibold px-4 py-1 rounded-full w-fit", GREY)}
        >
          Spam Lead
        </Badge>
      );

    if (
      lead.tags?.[0] &&
      lead.tags?.[0] === EContactSaleSubmissionTag.DUPLICATED_LEAD
    )
      return (
        <Badge
          className={cn("font-semibold px-4 py-1 rounded-full w-fit", WARM)}
        >
          Warm Lead
        </Badge>
      );
    if (
      lead.tags?.[0] &&
      lead.tags?.[0] === EContactSaleSubmissionTag.AGENT_LEAD &&
      lead.ceaInfo
    )
      return (
        <Badge
          className={cn("font-semibold px-4 py-1 rounded-full w-fit", GREY)}
        >
          {`Agent Lead ${lead.isEcbAgent ? "(ECB)" : "(JMA)"}`}
        </Badge>
      );

    if (lead.tags?.[0] && lead.tags?.[0] !== EContactSaleSubmissionTag.HOT_LEAD)
      return (
        <Badge
          className={cn("font-semibold px-4 py-1 rounded-full w-fit", GREY)}
        >
          {getLeadTag(lead.tags?.[0] || "")}
        </Badge>
      );

    return (
      <Badge className={cn("font-semibold px-4 py-1 rounded-full w-fit", RED)}>
        Hot Lead
      </Badge>
    );
  }, [
    lead.tags,
    lead.contactSaleSubmissionReport,
    lead.ceaInfo,
    lead.isEcbAgent,
  ]);

  const renderOtpStatus = useCallback(() => {
    if (!lead.verificationStatus) return null;
    const colorStatus =
      lead.verificationStatus === EOTPStatus.PENDING
        ? GREY
        : lead.verificationStatus === EOTPStatus.VERIFIED
          ? GREEN
          : ORANGE;

    return (
      <Badge
        className={cn(
          "font-semibold px-4 py-1 rounded-full w-fit",
          colorStatus
        )}
      >
        {lead.verificationStatus === EOTPStatus.PENDING
          ? "Pending"
          : lead.verificationStatus === EOTPStatus.VERIFIED
            ? "Lead Verified via OTP"
            : "Unverified"}
      </Badge>
    );
  }, [lead.verificationStatus]);

  return (
    <div
      className={cn(
        "rounded-xl border border-gray-200 bg-white p-4 shadow-sm w-full",
        isViewInDetail && "md:border md:border-black"
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex w-full items-center justify-between">
          <div className="text-gray-500 text-xs flex flex-row items-center justify-center">
            <span className="min-w-fit font-semibold">
              {convertToSingaporeTime(
                new Date(lead.createdAt),
                "DD/MM/YYYY hh:mm:ss A",
              )}
            </span>
            <span className="mx-1">•</span>
            <span className="min-w-fit">{getLeadSourceDisplay(lead)}</span>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            {isAdmin ? (
              <DeletePopover
                title={`Delete Enquiry`}
                description={`Are you sure you want to delete this enquiry?`}
                onDelete={() => onDelete(lead.id)}
                iconVariant="text"
                iconClass="h-5 w-5"
              />
            ) : profile?.role === Roles.AGENCY ? (
              <DeletePopover
                title="Delete Enquiry"
                description={
                  <>
                    <p>Please contact your account manager to delete leads.</p>
                    <p>Thank you!</p>
                  </>
                }
                onDelete={() => {}}
                iconVariant="text"
                iconClass="h-5 w-5"
                noAction
              />
            ) : null}
            {showCheckbox && (
              <Checkbox
                checked={isSelected}
                onChange={onSelect}
                className="cursor-pointer"
                size="sm"
              />
            )}
          </div>
        </div>
        {/* Lead Type Badge */}
        <div className="w-full flex flex-row items-center justify-start gap-2">
          {renderBadge()}
          {renderOtpStatus()}
        </div>
        {/* Lead Name and Contact */}
        <div className="flex flex-row items-center justify-between gap-2 mb-2">
          <div className="w-max-[60%] min-w-[180px] break-words">
            <Text className="text-2xl font-bold text-gray-900">
              {lead.name ? capitalizeWords(lead.name) : "N/A"}
            </Text>
            {lead.email && (
              <Text className="text-gray-500">{lead.email.toLowerCase()}</Text>
            )}
            {lead.phone && (
              <div className="flex gap-1 items-center">
                <Text className="text-gray-500">{formatPhone(lead.phone)}</Text>
                {lead.verificationStatus === EOTPStatus.VERIFIED && (
                  <Tooltip
                    size="md"
                    content="Lead Verified via OTP"
                    placement="top"
                    color="invert"
                  >
                    <span>
                      <VscVerifiedFilled className="text-blue-400 w-[18px] h-[18px]" />
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          {/* Contact buttons */}
          <div className="flex flex-shrink-0 flex-row items-center justify-center rounded-full overflow-hidden h-[54px] min-w-fit bg-green-500 text-white">
            <Button
              variant="text"
              size="lg"
              className="flex gap-1 flex-col items-center justify-center h-full"
              onClick={handleCallClick}
            >
              <PhoneIcon className="w-4 h-4" />
              <span className="text-xs">Call</span>
            </Button>
            <Button
              size="lg"
              variant="text"
              className="flex gap-1 flex-col items-center justify-center h-full pl-0"
              onClick={handleWhatsAppClick}
            >
              <RiWhatsappLine size={20} />
              <span className="text-xs">WhatsApp</span>
            </Button>
          </div>
        </div>
        {/* Project Info */}
        {lead.project && (
          <div className="w-full mb-2">
            <Text
              className={cn(
                "rounded-full p-2 font-semibold max-w-fit text-ellipsis overflow-hidden text-nowrap",
                lead.project.categoryId ===
                  import.meta.env.VITE_EXECUTIVE_CONDOMINIUM_CATEGORY_ID
                  ? "bg-blue-600 text-white"
                  : "bg-blue-50 text-blue-600",
              )}
            >
              {typeof lead.project.name === "object"
                ? lead.project.name.en
                : lead.project.name}
            </Text>
          </div>
        )}
        {renderAgent()}
        {/* Action Buttons */}
        <div className="flex items-center justify-between w-full">
          <Button
            className="text-gray-500 p-0 max-h-fit"
            variant="text"
            type="button"
            onClick={handleNotesClick}
          >
            Add a note...
          </Button>
          <Button
            variant="text"
            className="text-lg text-gray-900 font-semibold p-0 max-h-fit md:hidden"
            onClick={handleDetailsClick}
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
}
